import { useState, useEffect } from 'react';
import { useSocketStore } from '../stores/socketStore';
import { useAuthStore } from '../stores/authStore';

// Types pour le profil
export interface UserProfile {
  id: string;
  spotifyId: string | null;
  displayName: string;
  avatarUrl: string | null;
  isGuest: boolean;
  isPremium: boolean;
  level: number;
  xp: number;
  
  // Stats
  totalScore: number;
  gamesPlayed: number;
  gamesWon: number;
  winRate: number;
  titlesFound: number;
  artistsFound: number;
  perfectGuesses: number;
  currentStreak: number;
  bestStreak: number;
  avgResponseTime: number;
  
  // Ranks
  rankScore: number;
  rankWins: number;
  rankTitles: number;
  
  // Badges
  badges: UserBadge[];
  
  // Recent games
  recentGames: GameHistory[];
  
  // Dates
  createdAt: string;
  lastLogin: string | null;
}

export interface UserBadge {
  id: string;
  name: string;
  description: string;
  icon: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  earnedAt: string;
}

export interface GameHistory {
  id: string;
  roomName: string;
  gameMode: string;
  theme: string | null;
  finalScore: number;
  rank: number;
  titlesFound: number;
  perfectGuesses: number;
  playerCount: number;
  finishedAt: string;
}

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  requirementType: string;
  requirementValue: number;
  xpReward: number;
}

export function useProfile() {
  const { socket, isConnected } = useSocketStore();
  const { isAuthenticated, player } = useAuthStore();
  
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [allBadges, setAllBadges] = useState<Badge[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Récupérer le profil
  const fetchProfile = () => {
    if (!socket || !isConnected || !isAuthenticated) return;
    
    setIsLoading(true);
    setError(null);
    
    socket.emit('player:getProfile');
  };
  
  // Récupérer tous les badges
  const fetchAllBadges = () => {
    if (!socket || !isConnected) return;
    socket.emit('badges:getAll');
  };
  
  // Écouter les réponses
  useEffect(() => {
    if (!socket) return;
    
    const handleProfile = ({ profile: profileData }: { player: any; profile: UserProfile | null }) => {
      setProfile(profileData);
      setIsLoading(false);
    };
    
    const handleBadges = ({ badges }: { badges: Badge[] }) => {
      setAllBadges(badges);
    };
    
    socket.on('player:profile', handleProfile);
    socket.on('badges:list', handleBadges);
    
    return () => {
      socket.off('player:profile', handleProfile);
      socket.off('badges:list', handleBadges);
    };
  }, [socket]);
  
  // Charger automatiquement au mount
  useEffect(() => {
    if (isConnected && isAuthenticated && player?.type === 'spotify') {
      fetchProfile();
      fetchAllBadges();
    }
  }, [isConnected, isAuthenticated, player?.type]);
  
  return {
    profile,
    allBadges,
    isLoading,
    error,
    fetchProfile,
    fetchAllBadges,
  };
}

// Helper pour formater le temps de réponse
export function formatResponseTime(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// Helper pour obtenir la couleur de rareté
export function getRarityColor(rarity: string): string {
  switch (rarity) {
    case 'common': return 'text-gray-400';
    case 'uncommon': return 'text-green-400';
    case 'rare': return 'text-blue-400';
    case 'epic': return 'text-purple-400';
    case 'legendary': return 'text-yellow-400';
    default: return 'text-gray-400';
  }
}

// Helper pour obtenir le bg de rareté
export function getRarityBg(rarity: string): string {
  switch (rarity) {
    case 'common': return 'bg-gray-500/20 border-gray-500/30';
    case 'uncommon': return 'bg-green-500/20 border-green-500/30';
    case 'rare': return 'bg-blue-500/20 border-blue-500/30';
    case 'epic': return 'bg-purple-500/20 border-purple-500/30';
    case 'legendary': return 'bg-yellow-500/20 border-yellow-500/30 animate-pulse';
    default: return 'bg-gray-500/20';
  }
}
